/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StudioCampaign, StudioChannel } from "../types";
import { STUDIO_CHANNELS } from "../types";
import { STUB_SEGMENTS } from "../stub";
import { CHANNEL_META, Field } from "../shared";
import { estimateReach } from "../checks";

type AudienceStepProps = {
  campaign: StudioCampaign;
  onChange: (patch: Partial<StudioCampaign>) => void;
};

export const AudienceStep = defineComponent<AudienceStepProps>({
  name: "CampaignAudienceStep",
  props: ["campaign", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const segment = computed(() => STUB_SEGMENTS.find((s) => s.id === props.campaign.segment_id) ?? STUB_SEGMENTS[0]);
    const reach = computed(() => estimateReach(props.campaign, segment.value));

    const toggleChannel = (ch: StudioChannel) => {
      const on = props.campaign.channels.includes(ch);
      props.onChange({
        channels: on ? props.campaign.channels.filter((c) => c !== ch) : [...props.campaign.channels, ch],
      });
    };

    const moveFallback = (index: number, dir: -1 | 1) => {
      const order = [...props.campaign.fallback_order];
      const [item] = order.splice(index, 1);
      order.splice(index + dir, 0, item);
      props.onChange({ fallback_order: order });
    };

    const Segments = () => (
      <section class="space-y-3">
        <h3 class="text-lg font-semibold">{t("campaigns.studio_audience.who")}</h3>
        <div class="grid gap-2 sm:grid-cols-2">
          {STUB_SEGMENTS.map((s) => {
            const active = s.id === props.campaign.segment_id;
            return (
              <button
                key={s.id}
                type="button"
                class={[
                  "flex items-start gap-3 rounded-xl border-2 bg-base-100 p-3 text-left transition",
                  active ? "border-primary shadow" : "border-base-300 hover:border-primary/40",
                ]}
                onClick={() => props.onChange({ segment_id: s.id })}
              >
                <input type="radio" tabindex={-1} class="radio radio-primary radio-sm pointer-events-none mt-1" checked={active} />
                <div class="min-w-0 flex-1">
                  <div class="flex items-baseline justify-between gap-2">
                    <span class="font-semibold">{s.name}</span>
                    <span class="text-sm tabular-nums text-base-content/60">{s.size.toLocaleString("de-CH")}</span>
                  </div>
                  <div class="text-sm text-base-content/60">{s.description}</div>
                </div>
              </button>
            );
          })}
          <button
            type="button"
            disabled
            class="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-base-300 p-3 text-sm text-base-content/50"
          >
            <span class="material-symbols-outlined">add</span>
            {t("campaigns.studio_audience.new_segment")}
          </button>
        </div>
      </section>
    );

    const Channels = () => (
      <section class="space-y-3">
        <h3 class="text-lg font-semibold">{t("campaigns.studio_audience.channels")}</h3>
        <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {STUDIO_CHANNELS.map((ch) => {
            const on = props.campaign.channels.includes(ch);
            const reachable = segment.value.reachable[ch];
            const pct = segment.value.size ? Math.round((reachable / segment.value.size) * 100) : 0;
            return (
              <button
                key={ch}
                type="button"
                class={[
                  "rounded-xl border-2 bg-base-100 p-3 text-left transition",
                  on ? "border-primary" : "border-base-300 opacity-70 hover:opacity-100",
                ]}
                onClick={() => toggleChannel(ch)}
              >
                <div class="flex items-center justify-between">
                  <span class={["material-symbols-outlined", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
                  <input type="checkbox" tabindex={-1} class="checkbox checkbox-primary checkbox-sm pointer-events-none" checked={on} />
                </div>
                <div class="mt-2 font-semibold">{t(CHANNEL_META[ch].label)}</div>
                <div class="text-xs text-base-content/60">
                  {t("campaigns.studio_audience.reachable", [reachable.toLocaleString("de-CH"), pct])}
                </div>
                <progress class="progress progress-primary mt-2 h-1.5" value={pct} max={100} />
              </button>
            );
          })}
        </div>
        <p class="text-xs text-base-content/50">{t("campaigns.studio_audience.consent_note")}</p>
      </section>
    );

    const Strategy = () => (
      <section class="space-y-3">
        <h3 class="text-lg font-semibold">{t("campaigns.studio_audience.delivery")}</h3>
        <div class="grid gap-2 sm:grid-cols-2">
          {(["fallback", "all"] as const).map((s) => (
            <label
              key={s}
              class={[
                "flex cursor-pointer gap-3 rounded-xl border-2 bg-base-100 p-3",
                props.campaign.strategy === s ? "border-primary" : "border-base-300",
              ]}
            >
              <input
                type="radio"
                class="radio radio-primary radio-sm mt-1"
                checked={props.campaign.strategy === s}
                onChange={() => props.onChange({ strategy: s })}
              />
              <div>
                <div class="font-semibold">{t(`campaigns.studio_audience.strategy_${s}`)}</div>
                <div class="text-sm text-base-content/60">{t(`campaigns.studio_audience.strategy_${s}_hint`)}</div>
              </div>
            </label>
          ))}
        </div>
        {props.campaign.strategy === "fallback" && (
          <div class="rounded-xl bg-base-200 p-3">
            <div class="mb-2 text-sm font-medium">{t("campaigns.studio_audience.fallback_order")}</div>
            <ol class="flex flex-wrap items-center gap-2">
              {props.campaign.fallback_order.map((ch, i, all) => {
                const enabled = props.campaign.channels.includes(ch);
                return [
                  <li
                    key={ch}
                    class={["flex items-center gap-1 rounded-lg border border-base-300 bg-base-100 py-1 pl-2 pr-1 text-sm", !enabled && "opacity-40 line-through"]}
                  >
                    <span class={["material-symbols-outlined text-base", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
                    {t(CHANNEL_META[ch].label)}
                    <button type="button" class="btn btn-ghost btn-xs btn-square" disabled={i === 0} onClick={() => moveFallback(i, -1)}>
                      <span class="material-symbols-outlined text-sm">chevron_left</span>
                    </button>
                    <button type="button" class="btn btn-ghost btn-xs btn-square" disabled={i === all.length - 1} onClick={() => moveFallback(i, 1)}>
                      <span class="material-symbols-outlined text-sm">chevron_right</span>
                    </button>
                  </li>,
                  i < all.length - 1 ? <span class="material-symbols-outlined text-base-content/40">arrow_forward</span> : null,
                ];
              })}
            </ol>
          </div>
        )}
        <Field
          label={t("campaigns.studio_audience.holdout")}
          counter={`${props.campaign.holdout_percent}%`}
          hint={t("campaigns.studio_audience.holdout_hint")}
        >
          <input
            type="range"
            min={0}
            max={30}
            step={5}
            class="range range-primary range-sm"
            value={props.campaign.holdout_percent}
            onInput={(e) => props.onChange({ holdout_percent: Number((e.target as HTMLInputElement).value) })}
          />
        </Field>
      </section>
    );

    const Summary = () => (
      <aside class="sticky top-0 space-y-3 rounded-2xl border border-base-300 bg-base-100 p-5">
        <div class="text-sm text-base-content/60">{t("campaigns.studio_audience.estimated_reach")}</div>
        <div class="text-4xl font-bold tabular-nums">{reach.value.people.toLocaleString("de-CH")}</div>
        <div class="text-sm text-base-content/60">
          {t("campaigns.studio_audience.of_segment", [segment.value.size.toLocaleString("de-CH"), reach.value.holdout])}
        </div>
        <div class="divider my-1" />
        {STUDIO_CHANNELS.filter((ch) => reach.value.messages[ch] !== undefined).map((ch) => (
          <div key={ch} class="flex items-center justify-between text-sm">
            <span class="flex items-center gap-2">
              <span class={["material-symbols-outlined text-base", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
              {t(CHANNEL_META[ch].label)}
            </span>
            <span class="tabular-nums">{reach.value.messages[ch]!.toLocaleString("de-CH")}</span>
          </div>
        ))}
        {props.campaign.channels.length === 0 && (
          <div class="text-sm text-error">{t("campaigns.studio_checks.no_channels")}</div>
        )}
      </aside>
    );

    return () => (
      <div class="mx-auto grid max-w-6xl gap-8 p-6 lg:grid-cols-[1fr_280px]">
        <div class="space-y-8">
          {Segments()}
          {Channels()}
          {Strategy()}
        </div>
        <div>{Summary()}</div>
      </div>
    );
  },
});
